import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import { SelectSitelifeOptionType } from 'src/common/components/SelectSitelife/types';
import { Company } from 'src/company/types';
import {
  LicenseType,
  ProjectFilter,
  ProjectArchiveStatus,
  ProjectWithDownloadRef,
} from 'src/project/types/projects';
import { User } from 'src/user/types';

export function filterAndSortProjects(
  projects: ProjectWithDownloadRef[],
  search: string,
  sortDirection: 'asc' | 'desc',
  sortProperty: 'name' | 'createdAt',
) {
  return projects
    .filter((project) =>
      project.name.toLowerCase().includes(search.toLowerCase()),
    )
    .sort((a, b) => {
      const normalizedA = a[sortProperty]?.toLowerCase();
      const normalizedB = b[sortProperty]?.toLowerCase();
      if (normalizedA === undefined || normalizedB === undefined) {
        return 0;
      }
      if (normalizedA < normalizedB) return sortDirection === 'asc' ? -1 : 1;
      if (normalizedA > normalizedB) return sortDirection === 'asc' ? 1 : -1;

      return 0;
    });
}

export function getProjectFilterOptions(
  projects: ProjectWithDownloadRef[],
  t: TFunction<'project'>,
) {
  const typeOptions: SelectSitelifeOptionType[] = [];
  const statusOptions: SelectSitelifeOptionType[] = [];

  // Always include all project types
  const hasFreeProject = projects.some(
    (p) => p.license.type === LicenseType.FREE,
  );
  if (hasFreeProject) {
    typeOptions.push({
      label: t('project:filter.licenseType.free' as any),
      value: ProjectFilter.FREE,
    });
  }

  const hasBasicProject = projects.some(
    (p) => p.license.type === LicenseType.BASIC,
  );
  if (hasBasicProject) {
    typeOptions.push({
      label: t('project:filter.licenseType.basic' as any),
      value: ProjectFilter.BASIC,
    });
  }

  const hasDemoProject = projects.some(
    (p) => p.license.type === LicenseType.DEMO,
  );
  if (hasDemoProject) {
    typeOptions.push({
      label: t('project:filter.licenseType.demo' as any),
      value: ProjectFilter.DEMO,
    });
  }

  // Always include both status options
  statusOptions.push({
    label: t('project:filter.archiveStatus.active' as any),
    value: ProjectArchiveStatus.ACTIVE,
  });
  statusOptions.push({
    label: t('project:filter.archiveStatus.archived' as any),
    value: ProjectArchiveStatus.ARCHIVED,
  });

  return { typeOptions, statusOptions };
}

export function getProjectFilterLabel(
  filter: ProjectFilter,
  t: TFunction,
): string {
  switch (filter) {
    case ProjectFilter.FREE:
      return t('project:filter.licenseType.free' as any);
    case ProjectFilter.BASIC:
      return t('project:filter.licenseType.basic' as any);
    case ProjectFilter.DEMO:
      return t('project:filter.licenseType.demo' as any);
    default:
      return filter;
  }
}

export function getProjectArchiveStatusLabel(
  status: ProjectArchiveStatus,
  t: TFunction,
): string {
  switch (status) {
    case ProjectArchiveStatus.ACTIVE:
      return t('project:filter.archiveStatus.active' as any);
    case ProjectArchiveStatus.ARCHIVED:
      return t('project:filter.archiveStatus.archived' as any);
    default:
      return status;
  }
}

export function filterProjectsByExpiration(projects: ProjectWithDownloadRef[]) {
  return projects.filter((project) => {
    const isExpired = Boolean(
      project.license?.expirationDate &&
        dayjs(project.license?.expirationDate)
          .startOf('day')
          .isBefore(dayjs().startOf('day')),
    );

    return !isExpired;
  });
}

export function filterProjectsByTypeAndStatus(
  projects: ProjectWithDownloadRef[],
  licenseTypes: ProjectFilter[] | null,
  archiveStatuses: ProjectArchiveStatus[] | null,
) {
  let filteredProjects = [...projects];

  // Filter by license type if specified
  if (licenseTypes && licenseTypes.length > 0) {
    filteredProjects = filteredProjects.filter((project) =>
      licenseTypes.includes(project.license?.type as unknown as ProjectFilter),
    );
  }

  // Filter by archive status if specified
  if (archiveStatuses && archiveStatuses.length > 0) {
    filteredProjects = filteredProjects.filter((project) => {
      const licenseType = project.license?.type;
      const expirationDate = project.license?.expirationDate;

      // Calculate if project is expired
      // FREE: compare full timestamp (archiving sets expiration to now)
      // BASIC: compare by day only (project stays active on its last day)
      // DEMO: never expired (expires in 2099)
      let isExpired = false;
      if (licenseType === LicenseType.FREE && expirationDate) {
        isExpired = dayjs(expirationDate).isBefore(dayjs());
      } else if (licenseType === LicenseType.BASIC && expirationDate) {
        const expirationDay = dayjs(expirationDate).startOf('day');
        const currentDay = dayjs().startOf('day');

        if (expirationDay.isSame(currentDay, 'day')) {
          // Same day: project is still active on its last day
          isExpired = false;
        } else {
          // Different day: compare by day only
          isExpired = expirationDay.isBefore(currentDay);
        }
      }
      // DEMO projects are never expired (they expire in 2099)

      if (
        archiveStatuses.includes(ProjectArchiveStatus.ACTIVE) &&
        archiveStatuses.includes(ProjectArchiveStatus.ARCHIVED)
      ) {
        // Both selected - show all
        return true;
      } else if (archiveStatuses.includes(ProjectArchiveStatus.ACTIVE)) {
        // Only active
        return !isExpired;
      } else if (archiveStatuses.includes(ProjectArchiveStatus.ARCHIVED)) {
        // Only archived
        return isExpired;
      }
      return true;
    });
  }

  return filteredProjects;
}

export function groupProjectsByCompany(
  allProjects: ProjectWithDownloadRef[],
  user: User,
  userCompanies: Company[] | undefined | null,
) {
  const map: {
    name: string;
    companyId?: string;
    isWorkspace: boolean;
    isAdmin: boolean;
    projects: ProjectWithDownloadRef[];
  }[] = [];

  const { _id: userId, personalCompany } = user;

  if (userCompanies && userId) {
    userCompanies.forEach((company) => {
      map.push({
        name: company.name,
        companyId: company._id,
        isWorkspace: company._id === personalCompany,
        isAdmin: company.owner === userId || company.admins.includes(userId),
        projects: allProjects.filter(
          (p) =>
            p.company &&
            (typeof p.company === 'string'
              ? p.company === company._id
              : p.company._id === company._id),
        ),
      });
    });
  }

  const guestProjects = allProjects.filter((project) => {
    const projectCompanyId =
      typeof project.company === 'string'
        ? project.company
        : project.company?._id;

    return !userCompanies?.some((company) => company._id === projectCompanyId);
  });

  const groupedGuestProjects = guestProjects.reduce(
    (acc: Record<string, ProjectWithDownloadRef[]>, project) => {
      if (!project.company) return acc;
      if (typeof project.company === 'string') return acc;

      const { name } = project.company;

      return {
        ...acc,
        [name]: acc.hasOwnProperty(name) ? [...acc[name], project] : [project],
      };
    },
    {},
  );

  Object.keys(groupedGuestProjects).forEach((key) => {
    map.push({
      name: key,
      isWorkspace: false,
      isAdmin: false,
      projects: groupedGuestProjects[key],
    });
  });

  map.sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
  const personalCompanyIndex = map.findIndex(
    (c) => c.companyId === personalCompany,
  );
  map.push(map[personalCompanyIndex]);
  map.splice(personalCompanyIndex, 1);

  return map;
}
